import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import {
  getPackage, getPackageItems, getPackageSupplierMatches,
  getRFQs, getQuotes, getPackageBidLeveling, getPackageAwardRecommendation,
  getPackagePostAward, updatePostAward, transitionPackage,
  addItemsToPackage, getProjectItems, approveAward, runBidLeveling,
  generateAwardRecommendation, updatePackage
} from '../utils/api';
import LifecyclePill from '../components/LifecyclePill';
import ConfidenceBadge from '../components/ConfidenceBadge';
import AuditTrail from '../components/AuditTrail';

const TABS = [
  { id: 'overview',     label: 'Overview'     },
  { id: 'scope',        label: 'Scope'        },
  { id: 'items',        label: 'Items'        },
  { id: 'suppliers',    label: 'Suppliers'    },
  { id: 'rfqs',         label: 'RFQs'         },
  { id: 'quotes',       label: 'Quotes'       },
  { id: 'bid-leveling', label: 'Bid Leveling' },
  { id: 'award',        label: 'Award'        },
  { id: 'post-award',   label: 'Post-Award'   },
  { id: 'activity',     label: 'Activity'     },
];

const LIFECYCLE_NEXT = {
  draft_scope:          'scope_review',
  scope_review:         'ready_for_rfq',
  ready_for_rfq:        'rfq_drafted',
  rfq_drafted:          'rfq_approved',
  rfq_approved:         'rfq_sent',
  rfq_sent:             'awaiting_quotes',
  awaiting_quotes:      'quotes_received',
  quotes_received:      'bid_leveling',
  bid_leveling:         'award_recommendation',
  award_recommendation: 'award_approved',
  award_approved:       'contract_po_pending',
};

const fmt = n => n ? '£' + Number(n).toLocaleString() : '—';

export default function PackageDetail() {
  const { id } = useParams();
  const { selectedProject, addToast } = useOutletContext();
  const navigate = useNavigate();

  const [tab, setTab] = useState('overview');
  const [pkg, setPkg] = useState(null);
  const [items, setItems] = useState([]);
  const [allProjectItems, setAllProjectItems] = useState([]);
  const [supplierMatches, setSupplierMatches] = useState([]);
  const [rfqs, setRfqs] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [bidLeveling, setBidLeveling] = useState(null);
  const [awardRec, setAwardRec] = useState(null);
  const [postAward, setPostAward] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transitioning, setTransitioning] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showAssign, setShowAssign] = useState(false);
  const [editingScope, setEditingScope] = useState(false);
  const [scopeText, setScopeText] = useState('');
  const [message, setMessage] = useState(null);

  useEffect(() => { loadPackage(); }, [id]);
  useEffect(() => { if (tab === 'items' && selectedProject?.id) loadAllProjectItems(); }, [tab, selectedProject]);

  async function loadPackage() {
    setLoading(true);
    try {
      const [pkgData, itemsData, matchesData, rfqsData, quotesData, blData, arData, paData] = await Promise.all([
        getPackage(id),
        getPackageItems(id),
        getPackageSupplierMatches(id).catch(() => []),
        getRFQs(id).catch(() => []),
        getQuotes(id).catch(() => []),
        getPackageBidLeveling(id).catch(() => null),
        getPackageAwardRecommendation(id).catch(() => null),
        getPackagePostAward(id).catch(() => null),
      ]);
      setPkg(pkgData);
      setItems(itemsData || []);
      setSupplierMatches(matchesData || []);
      setRfqs(rfqsData || []);
      setQuotes(quotesData || []);
      setBidLeveling(blData);
      setAwardRec(arData);
      setPostAward(paData);
      setScopeText(pkgData?.human_approved_scope || pkgData?.ai_generated_scope || pkgData?.scope_summary || '');
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }

  async function loadAllProjectItems() {
    try {
      const data = await getProjectItems(selectedProject.id);
      setAllProjectItems((data || []).filter(i => !i.package_id || i.package_id === parseInt(id)));
    } catch (err) { console.error(err); }
  }

  async function handleTransition(toState) {
    setTransitioning(true);
    try {
      await transitionPackage(id, toState);
      await loadPackage();
      setMessage({ type: 'success', text: `Package moved to: ${toState.replace(/_/g, ' ')}` });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
    setTransitioning(false);
  }

  async function handleAssignItems() {
    if (!selectedItems.length) return;
    try {
      await addItemsToPackage(id, selectedItems);
      setSelectedItems([]);
      setShowAssign(false);
      await loadPackage();
      setMessage({ type: 'success', text: `${selectedItems.length} items assigned` });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  }

  async function handleSaveScope() {
    try {
      await updatePackage(id, { ...pkg, human_approved_scope: scopeText, scope_summary: scopeText });
      setEditingScope(false);
      await loadPackage();
      setMessage({ type: 'success', text: 'Scope saved' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  }

  async function handleRunBidLeveling() {
    try {
      await runBidLeveling(id);
      await loadPackage();
      setMessage({ type: 'success', text: 'Bid leveling complete' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  }

  async function handleGenerateAward() {
    try {
      await generateAwardRecommendation(id);
      await loadPackage();
      setMessage({ type: 'success', text: 'Award recommendation generated' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  }

  async function handleApproveAward() {
    if (!awardRec) return;
    try {
      await approveAward(id, awardRec.id, '');
      await handleTransition('award_approved');
      setMessage({ type: 'success', text: 'Award approved — package moving to Contract/PO' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  }

  async function handleUpdatePostAward(field, value) {
    try {
      const updated = { ...postAward, [field]: value };
      await updatePostAward(id, updated);
      setPostAward(updated);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  }

  if (loading) return <div className="page"><div className="empty-state"><div className="spinner" /></div></div>;
  if (!pkg) return <div className="page"><div className="empty-state"><h3>Package not found</h3><button onClick={() => navigate('/packages')} className="btn btn-primary">← Back</button></div></div>;

  const state = pkg.lifecycle_state || 'draft_scope';
  const nextState = LIFECYCLE_NEXT[state];
  const nextStateLabel = nextState ? nextState.replace(/_/g, ' ') : null;

  return (
    <div className="page">

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, gap: 16 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <button onClick={() => navigate('/packages')} className="btn btn-ghost btn-sm">← Packages</button>
            <span style={{ color: 'var(--color-border-mid)' }}>›</span>
            <span style={{ fontSize: '0.875rem', color: 'var(--color-text-mid)' }}>
              PKG-{String(pkg.id).padStart(3, '0')}
            </span>
          </div>
          <h1 style={{ fontSize: '1.375rem', fontWeight: 700, marginBottom: 6 }}>{pkg.name}</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <LifecyclePill state={state} size="lg" />
            {pkg.trade_category && (
              <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-mid)' }}>{pkg.trade_category}</span>
            )}
            {pkg.risk_level && pkg.risk_level !== 'low' && (
              <span className={`badge badge-${pkg.risk_level === 'high' ? 'error' : 'warning'}`}>
                {pkg.risk_level} risk
              </span>
            )}
          </div>
        </div>

        {/* Next action button */}
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          {nextStateLabel && (
            <button
              onClick={() => handleTransition(nextState)}
              disabled={transitioning}
              className="btn btn-primary"
            >
              {transitioning ? '...' : `→ ${nextStateLabel.replace(/\b\w/g, c => c.toUpperCase())}`}
            </button>
          )}
        </div>
      </div>

      {/* Message */}
      {message && (
        <div style={{
          padding: '10px 16px', borderRadius: 'var(--radius-md)', fontSize: '0.875rem',
          fontWeight: 500, marginBottom: 16,
          background: message.type === 'success' ? '#ECFDF5' : '#FEF2F2',
          color: message.type === 'success' ? 'var(--color-success)' : 'var(--color-error)',
          border: `1px solid ${message.type === 'success' ? 'var(--color-success)' : 'var(--color-error)'}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          {message.text}
          <button onClick={() => setMessage(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', opacity: 0.5 }}>✕</button>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs" style={{ marginBottom: 20 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`tab-btn ${tab === t.id ? 'active' : ''}`}>
            {t.label}
            {t.id === 'items' && items.length > 0 && (
              <span style={{ marginLeft: 5, fontSize: '0.6875rem', background: 'var(--color-teal-light)', color: 'var(--color-teal)', padding: '1px 5px', borderRadius: 'var(--radius-full)' }}>
                {items.length}
              </span>
            )}
            {t.id === 'quotes' && quotes.length > 0 && (
              <span style={{ marginLeft: 5, fontSize: '0.6875rem', background: 'var(--color-teal-light)', color: 'var(--color-teal)', padding: '1px 5px', borderRadius: 'var(--radius-full)' }}>
                {quotes.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW TAB ─────────────────────────────────────────────────── */}
      {tab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Key metrics */}
            <div className="grid-3">
              {[
                { label: 'Items', value: items.length },
                { label: 'Quotes', value: quotes.length },
                { label: 'RFQs Sent', value: rfqs.length },
              ].map(m => (
                <div key={m.label} className="kpi-card">
                  <div className="kpi-card-label">{m.label}</div>
                  <div className="kpi-card-value">{m.value}</div>
                </div>
              ))}
            </div>

            {/* Scope summary */}
            {pkg.scope_summary && (
              <div className="card">
                <div className="card-header">
                  <span style={{ fontWeight: 600 }}>Scope Summary</span>
                  <span className="ai-badge">✦ AI</span>
                </div>
                <div className="card-body">
                  <p style={{ lineHeight: 1.7, color: 'var(--color-text-dark)' }}>{pkg.scope_summary}</p>
                </div>
              </div>
            )}

            {/* Dates */}
            {(pkg.rfq_due_date || pkg.execution_date || pkg.award_due_date) && (
              <div className="card">
                <div className="card-header"><span style={{ fontWeight: 600 }}>Key Dates</span></div>
                <div className="card-body">
                  <div className="grid-3">
                    {[
                      ['RFQ Due', pkg.rfq_due_date],
                      ['Execution', pkg.execution_date],
                      ['Award Due', pkg.award_due_date],
                      ['Delivery Needed', pkg.delivery_needed_date],
                    ].filter(([, v]) => v).map(([label, value]) => (
                      <div key={label} style={{ padding: '10px 14px', background: 'var(--color-background)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)' }}>
                        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-light)', marginBottom: 4 }}>{label}</div>
                        <div style={{ fontSize: '0.875rem', fontWeight: 600 }}>
                          {new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right: lifecycle progress */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="card">
              <div className="card-header"><span style={{ fontWeight: 600 }}>Lifecycle Progress</span></div>
              <div className="card-body">
                {[
                  { states: ['draft_scope','scope_review','ready_for_rfq'], label: 'Scope' },
                  { states: ['rfq_drafted','rfq_approved','rfq_sent'], label: 'RFQ' },
                  { states: ['awaiting_quotes','quotes_received'], label: 'Quotes' },
                  { states: ['bid_leveling','award_recommendation','award_approved'], label: 'Award' },
                  { states: ['contract_po_pending','contract_po_issued','submittals_pending','submittals_approved','delivery_scheduled','delivered','invoice_pending','invoice_matched','closed'], label: 'Post-Award' },
                ].map(stage => {
                  const allStates = ['draft_scope','scope_review','ready_for_rfq','rfq_drafted','rfq_approved','rfq_sent','awaiting_quotes','quotes_received','bid_leveling','award_recommendation','award_approved','contract_po_pending','contract_po_issued','submittals_pending','submittals_approved','delivery_scheduled','delivered','invoice_pending','invoice_matched','closed'];
                  const currentIdx = allStates.indexOf(state);
                  const stageIdxs = stage.states.map(s => allStates.indexOf(s));
                  const isComplete = stageIdxs.every(i => i < currentIdx);
                  const isCurrent = stageIdxs.includes(currentIdx);
                  return (
                    <div key={stage.label} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                      <div style={{
                        width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.6875rem', fontWeight: 700,
                        background: isComplete ? 'var(--color-success)' : isCurrent ? 'var(--color-teal)' : 'var(--color-border)',
                        color: isComplete || isCurrent ? '#fff' : 'var(--color-text-light)',
                      }}>
                        {isComplete ? '✓' : ''}
                      </div>
                      <span style={{ fontSize: '0.875rem', fontWeight: isCurrent ? 600 : 400, color: isCurrent ? 'var(--color-text-dark)' : 'var(--color-text-mid)' }}>
                        {stage.label}
                      </span>
                      {isCurrent && <LifecyclePill state={state} />}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── SCOPE TAB ────────────────────────────────────────────────────── */}
      {tab === 'scope' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card">
            <div className="card-header">
              <div>
                <span style={{ fontWeight: 600 }}>Package Scope</span>
                {pkg.ai_generated_scope && <span className="ai-badge" style={{ marginLeft: 8 }}>✦ AI Generated</span>}
              </div>
              <button onClick={() => setEditingScope(!editingScope)} className="btn btn-secondary btn-sm">
                {editingScope ? 'Cancel' : '✎ Edit'}
              </button>
            </div>
            <div className="card-body">
              {editingScope ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <textarea
                    className="form-textarea"
                    value={scopeText}
                    onChange={e => setScopeText(e.target.value)}
                    style={{ minHeight: 200, fontSize: '0.875rem', lineHeight: 1.7 }}
                    placeholder="Describe the full scope of work for this package..."
                  />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={handleSaveScope} className="btn btn-primary btn-sm">Save Scope</button>
                    <button onClick={() => setEditingScope(false)} className="btn btn-secondary btn-sm">Cancel</button>
                  </div>
                </div>
              ) : (
                <div>
                  {(pkg.human_approved_scope || pkg.ai_generated_scope || pkg.scope_summary) ? (
                    <p style={{ lineHeight: 1.8, color: 'var(--color-text-dark)', fontSize: '0.9375rem' }}>
                      {pkg.human_approved_scope || pkg.ai_generated_scope || pkg.scope_summary}
                    </p>
                  ) : (
                    <div className="empty-state" style={{ padding: 32 }}>
                      <p>No scope defined yet</p>
                      <button onClick={() => setEditingScope(true)} className="btn btn-primary btn-sm">+ Add Scope</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Transition to scope review */}
          {state === 'draft_scope' && (
            <div className="approval-gate">
              <div style={{ fontWeight: 600, marginBottom: 4, color: 'var(--color-warning)' }}>Ready for scope review?</div>
              <p style={{ fontSize: '0.8125rem', marginBottom: 12 }}>Once you're happy with the scope, submit for review to move this package forward.</p>
              <button onClick={() => handleTransition('scope_review')} disabled={transitioning} className="btn btn-primary btn-sm">
                Submit for Scope Review →
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── ITEMS TAB ────────────────────────────────────────────────────── */}
      {tab === 'items' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card">
            <div className="card-header">
              <span style={{ fontWeight: 600 }}>Assigned Items ({items.length})</span>
              <button onClick={() => setShowAssign(!showAssign)} className="btn btn-primary btn-sm">+ Assign Items</button>
            </div>
            {items.length === 0 ? (
              <div className="empty-state" style={{ padding: 32 }}>
                <div className="empty-state-icon">◈</div>
                <h3>No items assigned</h3>
                <p>Assign extracted items to this package</p>
                <button onClick={() => setShowAssign(true)} className="btn btn-primary btn-sm">+ Assign Items</button>
              </div>
            ) : (
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      {['Description', 'Trade', 'Qty', 'Unit', 'Confidence', 'Source'].map(h => <th key={h}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(item => (
                      <tr key={item.id}>
                        <td style={{ fontWeight: 500 }}>{item.item_name}</td>
                        <td><span className="badge badge-neutral">{item.trade_category}</span></td>
                        <td style={{ fontFamily: 'var(--font-mono)', textAlign: 'right' }}>{item.quantity || '—'}</td>
                        <td style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8125rem' }}>{item.unit || '—'}</td>
                        <td><ConfidenceBadge value={item.confidence || 0.5} /></td>
                        <td style={{ fontSize: '0.75rem', color: 'var(--color-text-light)' }}>
                          {item.source_document || '—'}
                          {item.source_page && ` p.${item.source_page}`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Assign items panel */}
          {showAssign && (
            <div className="card">
              <div className="card-header">
                <span style={{ fontWeight: 600 }}>Assign from Project Items</span>
                <button onClick={() => setShowAssign(false)} className="btn btn-ghost btn-sm">✕</button>
              </div>
              <div className="card-body">
                {allProjectItems.filter(i => !i.package_id).length === 0 ? (
                  <div className="empty-state" style={{ padding: 24 }}>
                    <p>No unassigned items available. Upload documents first to extract items.</p>
                    <button onClick={() => navigate('/documents')} className="btn btn-primary btn-sm">Upload Documents →</button>
                  </div>
                ) : (
                  <>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12, maxHeight: 300, overflowY: 'auto' }}>
                      {allProjectItems.filter(i => !i.package_id).map(item => {
                        const isSelected = selectedItems.includes(item.id);
                        return (
                          <div key={item.id}
                            onClick={() => setSelectedItems(prev => isSelected ? prev.filter(x => x !== item.id) : [...prev, item.id])}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                              border: `1px solid ${isSelected ? 'var(--color-teal)' : 'var(--color-border)'}`,
                              borderRadius: 'var(--radius-md)', cursor: 'pointer',
                              background: isSelected ? 'var(--color-teal-light)' : 'transparent',
                            }}>
                            <input type="checkbox" checked={isSelected} readOnly style={{ accentColor: 'var(--color-teal)' }} />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 500, fontSize: '0.875rem' }}>{item.item_name}</div>
                              <div style={{ fontSize: '0.75rem', color: 'var(--color-text-light)' }}>
                                {item.quantity} {item.unit} · {item.trade_category}
                              </div>
                            </div>
                            <ConfidenceBadge value={item.confidence || 0.5} />
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={handleAssignItems} disabled={!selectedItems.length} className="btn btn-primary btn-sm">
                        Assign {selectedItems.length > 0 ? `${selectedItems.length} items` : 'items'}
                      </button>
                      <button onClick={() => { setShowAssign(false); setSelectedItems([]); }} className="btn btn-secondary btn-sm">Cancel</button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── SUPPLIERS TAB ─────────────────────────────────────────────────── */}
      {tab === 'suppliers' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card">
            <div className="card-header">
              <span style={{ fontWeight: 600 }}>Supplier Matches ({supplierMatches.length})</span>
              <button onClick={() => navigate('/suppliers')} className="btn btn-secondary btn-sm">Manage Suppliers →</button>
            </div>
            {supplierMatches.length === 0 ? (
              <div className="empty-state" style={{ padding: 32 }}>
                <div className="empty-state-icon">◉</div>
                <h3>No supplier matches yet</h3>
                <p>Add suppliers to your directory — they'll be matched to this package automatically</p>
                <button onClick={() => navigate('/suppliers')} className="btn btn-primary btn-sm">Add Suppliers →</button>
              </div>
            ) : (
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>{['Supplier', 'Match Score', 'Reasons', 'RFQ Status'].map(h => <th key={h}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {supplierMatches.map(match => (
                      <tr key={match.id}>
                        <td style={{ fontWeight: 500 }}>{match.supplier_name}</td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ width: 60, height: 4, background: 'var(--color-border)', borderRadius: 2, overflow: 'hidden' }}>
                              <div style={{ width: `${(match.match_score || 0) * 100}%`, height: '100%', background: 'var(--color-teal)', borderRadius: 2 }} />
                            </div>
                            <span style={{ fontSize: '0.8125rem', fontFamily: 'var(--font-mono)' }}>{Math.round((match.match_score || 0) * 100)}%</span>
                          </div>
                        </td>
                        <td style={{ fontSize: '0.8125rem', color: 'var(--color-text-mid)' }}>
                          {match.match_reasons?.join(', ') || '—'}
                        </td>
                        <td>
                          <span className={`badge ${match.approved_for_rfq ? 'badge-success' : 'badge-neutral'}`}>
                            {match.approved_for_rfq ? 'Approved' : 'Pending'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── RFQS TAB ─────────────────────────────────────────────────────── */}
      {tab === 'rfqs' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card">
            <div className="card-header">
              <span style={{ fontWeight: 600 }}>RFQ Status ({rfqs.length})</span>
              <button onClick={() => navigate('/rfq')} className="btn btn-secondary btn-sm">Manage RFQs →</button>
            </div>
            {rfqs.length === 0 ? (
              <div className="empty-state" style={{ padding: 32 }}>
                <div className="empty-state-icon">✉</div>
                <h3>No RFQs sent yet</h3>
                <p>Send RFQs to suppliers from the RFQ page</p>
                <button onClick={() => navigate('/rfq')} className="btn btn-primary btn-sm">Go to RFQs →</button>
              </div>
            ) : (
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>{['Supplier', 'Status', 'Sent', 'Responded', 'Reminders'].map(h => <th key={h}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {rfqs.map(rfq => (
                      <tr key={rfq.id}>
                        <td style={{ fontWeight: 500 }}>{rfq.supplier_name || `Supplier #${rfq.supplier_id}`}</td>
                        <td><span className={`badge ${rfq.status === 'responded' ? 'badge-success' : 'badge-warning'}`}>{rfq.status}</span></td>
                        <td style={{ fontSize: '0.8125rem', color: 'var(--color-text-mid)' }}>{rfq.sent_at ? new Date(rfq.sent_at).toLocaleDateString() : '—'}</td>
                        <td style={{ fontSize: '0.8125rem', color: 'var(--color-text-mid)' }}>{rfq.responded_at ? new Date(rfq.responded_at).toLocaleDateString() : '—'}</td>
                        <td style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8125rem' }}>{rfq.reminder_count || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── QUOTES TAB ───────────────────────────────────────────────────── */}
      {tab === 'quotes' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card">
            <div className="card-header">
              <span style={{ fontWeight: 600 }}>Quotes Received ({quotes.length})</span>
            </div>
            {quotes.length === 0 ? (
              <div className="empty-state" style={{ padding: 32 }}>
                <div className="empty-state-icon">⚖</div>
                <h3>No quotes yet</h3>
                <p>Quotes will appear here once suppliers respond to RFQs</p>
              </div>
            ) : (
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>{['Supplier', 'Total Price', 'Lead Time', 'Valid Until', 'Exclusions', 'Status'].map(h => <th key={h}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {quotes.map(q => (
                      <tr key={q.id}>
                        <td style={{ fontWeight: 500 }}>{q.supplier_name || `Supplier #${q.supplier_id}`}</td>
                        <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{fmt(q.total_price)}</td>
                        <td style={{ fontFamily: 'var(--font-mono)' }}>{q.lead_time_days ? `${q.lead_time_days}d` : '—'}</td>
                        <td style={{ fontSize: '0.8125rem' }}>{q.validity_date ? new Date(q.validity_date).toLocaleDateString() : '—'}</td>
                        <td style={{ fontSize: '0.8125rem', color: 'var(--color-text-mid)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{q.exclusions || 'None stated'}</td>
                        <td><span className={`badge ${q.parse_status === 'processed' ? 'badge-success' : 'badge-neutral'}`}>{q.parse_status || 'pending'}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── BID LEVELING TAB ─────────────────────────────────────────────── */}
      {tab === 'bid-leveling' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {!bidLeveling ? (
            <div className="card">
              <div className="card-body" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: 40, textAlign: 'center' }}>
                <span className="ai-badge">✦ AI Bid Leveling</span>
                <h3>No bid leveling yet</h3>
                <p style={{ maxWidth: 400 }}>Once quotes are received, run bid leveling to compare quotes like-for-like and identify the best value option.</p>
                <button onClick={handleRunBidLeveling} disabled={quotes.length === 0} className="btn btn-primary">
                  {quotes.length === 0 ? 'Need quotes first' : '✦ Run Bid Leveling'}
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div className="card">
                <div className="card-header">
                  <div>
                    <span style={{ fontWeight: 600 }}>Bid Leveling Results</span>
                    <span className="ai-badge" style={{ marginLeft: 8 }}>✦ AI Analysis</span>
                  </div>
                  <span style={{ fontSize: '0.75rem', color: 'var(--color-text-light)' }}>
                    {new Date(bidLeveling.created_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="card-body">
                  <div className="grid-3" style={{ marginBottom: 16 }}>
                    {[
                      ['Best Price', bidLeveling.best_price_supplier, 'var(--color-success)'],
                      ['Best Value', bidLeveling.best_value_supplier, 'var(--color-teal)'],
                      ['Fastest', bidLeveling.fastest_supplier, 'var(--color-info)'],
                    ].map(([label, value, color]) => (
                      <div key={label} style={{ padding: '14px', background: 'var(--color-background)', borderRadius: 'var(--radius-md)', border: `1px solid var(--color-border)`, borderLeft: `3px solid ${color}` }}>
                        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-light)', marginBottom: 4 }}>{label}</div>
                        <div style={{ fontWeight: 600, color: 'var(--color-text-dark)' }}>{value || '—'}</div>
                      </div>
                    ))}
                  </div>
                  {bidLeveling.risk_flags && (
                    <div className="risk-flag risk-flag-medium">
                      <span>⚑</span>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '0.8125rem' }}>Risk Flags</div>
                        <div style={{ fontSize: '0.8125rem' }}>{JSON.stringify(bidLeveling.risk_flags)}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Quotes comparison table */}
              {quotes.length > 0 && (
                <div className="card">
                  <div className="card-header"><span style={{ fontWeight: 600 }}>Quote Comparison</span></div>
                  <div className="table-wrapper">
                    <table>
                      <thead>
                        <tr>{['Supplier', 'Total Price', 'Lead Time', 'Exclusions', 'Payment Terms'].map(h => <th key={h}>{h}</th>)}</tr>
                      </thead>
                      <tbody>
                        {[...quotes].sort((a, b) => (a.total_price || 0) - (b.total_price || 0)).map((q, i) => (
                          <tr key={q.id} style={{ background: i === 0 ? '#F0FDF4' : 'transparent' }}>
                            <td style={{ fontWeight: 500 }}>
                              {q.supplier_name || `Supplier #${q.supplier_id}`}
                              {i === 0 && <span className="badge badge-success" style={{ marginLeft: 8 }}>Best Price</span>}
                            </td>
                            <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: i === 0 ? 'var(--color-success)' : 'var(--color-text-dark)' }}>{fmt(q.total_price)}</td>
                            <td style={{ fontFamily: 'var(--font-mono)' }}>{q.lead_time_days ? `${q.lead_time_days}d` : '—'}</td>
                            <td style={{ fontSize: '0.8125rem', color: q.exclusions ? 'var(--color-warning)' : 'var(--color-text-light)' }}>{q.exclusions || 'None'}</td>
                            <td style={{ fontSize: '0.8125rem', color: 'var(--color-text-mid)' }}>{q.payment_terms || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── AWARD TAB ────────────────────────────────────────────────────── */}
      {tab === 'award' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {!awardRec ? (
            <div className="card">
              <div className="card-body" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: 40, textAlign: 'center' }}>
                <span className="ai-badge">✦ AI Award Recommendation</span>
                <h3>No award recommendation yet</h3>
                <p style={{ maxWidth: 400 }}>Run bid leveling first, then generate an AI award recommendation memo for human approval.</p>
                <button onClick={handleGenerateAward} disabled={!bidLeveling} className="btn btn-primary">
                  {!bidLeveling ? 'Run bid leveling first' : '✦ Generate Award Recommendation'}
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div className="approval-gate" style={{ borderColor: awardRec.human_approval_status === 'approved' ? 'var(--color-success)' : 'var(--color-warning)', background: awardRec.human_approval_status === 'approved' ? 'var(--color-success-bg)' : 'var(--color-warning-bg)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <span className="ai-badge">✦ AI Prepared</span>
                      <span style={{ fontSize: '0.75rem', fontWeight: 600, color: awardRec.human_approval_status === 'approved' ? 'var(--color-success)' : 'var(--color-warning)', textTransform: 'uppercase' }}>
                        {awardRec.human_approval_status === 'approved' ? '✓ Award Approved' : 'Pending Human Approval'}
                      </span>
                    </div>
                    <h3 style={{ marginBottom: 6 }}>Award to: {awardRec.recommended_supplier_name}</h3>
                    <p style={{ fontSize: '0.875rem', lineHeight: 1.6, marginBottom: 8 }}>{awardRec.recommendation_summary}</p>
                    {awardRec.reasoning && (
                      <div style={{ padding: '12px 14px', background: 'rgba(255,255,255,0.5)', borderRadius: 'var(--radius-md)', fontSize: '0.8125rem', lineHeight: 1.6 }}>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>Reasoning</div>
                        {awardRec.reasoning}
                      </div>
                    )}
                    {awardRec.risk_notes && (
                      <div className="risk-flag risk-flag-medium" style={{ marginTop: 10 }}>
                        <span>⚑</span>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '0.8125rem' }}>Risk Notes</div>
                          <div style={{ fontSize: '0.8125rem' }}>{awardRec.risk_notes}</div>
                        </div>
                      </div>
                    )}
                  </div>
                  {awardRec.human_approval_status !== 'approved' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
                      <button onClick={handleApproveAward} className="btn btn-approve">✓ Approve Award</button>
                      <button className="btn btn-danger btn-sm">✕ Reject</button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── POST-AWARD TAB ───────────────────────────────────────────────── */}
      {tab === 'post-award' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {!postAward ? (
            <div className="empty-state card" style={{ padding: 40 }}>
              <div className="empty-state-icon">◫</div>
              <h3>Post-award tracking not started</h3>
              <p>This section activates once the package is awarded and moves to Contract/PO stage</p>
            </div>
          ) : (
            <div className="card">
              <div className="card-header"><span style={{ fontWeight: 600 }}>Post-Award Checklist</span></div>
              <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {[
                  { key: 'contract_po_status', dueKey: 'contract_po_due', label: 'Contract / PO' },
                  { key: 'submittal_status', dueKey: 'submittal_due', label: 'Submittals' },
                  { key: 'delivery_status', dueKey: 'delivery_due', label: 'Delivery' },
                  { key: 'invoice_status', dueKey: 'invoice_due', label: 'Invoice' },
                ].map(item => (
                  <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '12px 16px', background: 'var(--color-background)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500, fontSize: '0.875rem', marginBottom: 4 }}>{item.label}</div>
                      <input
                        type="date"
                        className="form-input"
                        value={postAward[item.dueKey] || ''}
                        onChange={e => handleUpdatePostAward(item.dueKey, e.target.value)}
                        style={{ width: 160, fontSize: '0.8125rem', padding: '4px 8px' }}
                      />
                    </div>
                    <select
                      className="form-select"
                      value={postAward[item.key] || 'pending'}
                      onChange={e => handleUpdatePostAward(item.key, e.target.value)}
                      style={{ width: 140 }}
                    >
                      <option value="pending">Pending</option>
                      <option value="in_progress">In Progress</option>
                      <option value="complete">Complete</option>
                      <option value="overdue">Overdue</option>
                    </select>
                    <div style={{
                      width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                      background: postAward[item.key] === 'complete' ? 'var(--color-success)' :
                        postAward[item.key] === 'overdue' ? 'var(--color-error)' :
                        postAward[item.key] === 'in_progress' ? 'var(--color-warning)' : 'var(--color-border)',
                    }} />
                  </div>
                ))}
                {postAward.notes !== undefined && (
                  <div>
                    <label className="form-label">Notes</label>
                    <textarea
                      className="form-textarea"
                      value={postAward.notes || ''}
                      onChange={e => handleUpdatePostAward('notes', e.target.value)}
                      placeholder="Post-award notes..."
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── ACTIVITY TAB ─────────────────────────────────────────────────── */}
      {tab === 'activity' && (
        <div className="card">
          <div className="card-header"><span style={{ fontWeight: 600 }}>Activity & Audit Trail</span></div>
          <div className="card-body">
            <AuditTrail entityType="package" entityId={id} maxItems={50} />
          </div>
        </div>
      )}
    </div>
  );
}